// Two-step Klaviyo signup: email, then optional SMS. See decisions.md.

const NEWSLETTER_POPUP_SOURCE = 'Newsletter Popup';

const NEWSLETTER_POPUP_FREQUENCY_DAYS = 7;

const NEWSLETTER_SUBSCRIBED_KEY = 'newsletterSubscribed';
const NEWSLETTER_TRIGGER_HIDDEN_KEY = 'newsletterTriggerHiddenUntil';

class NewsletterPopup extends HTMLElement {
  /**
    * Console helper for dev/QA: `NewsletterPopup.open()` opens the popup on any
   * page and clears the frequency suppression first, so it can be reopened as
   * many times as needed without wiping localStorage by hand.
   *
   * To see each state on the storefront, run in the browser console:
   *
   *   p = NewsletterPopup.open()      // step 1: email (run this first)
   *   p.showStep(p.smsStep)           // step 2: SMS
   *   p.showStep(p.successState)      // thank-you
   *   p.showStep(p.emailStep)         // back to step 1
   *
   * Side tab back: remove localStorage 'newsletterSubscribed' and
   * 'newsletterTriggerHiddenUntil', then reload.
   */
  static open() {
    localStorage.removeItem('newsletterPopupClosed');
    localStorage.removeItem('newsletterPopupExpiry');

    const popup = document.querySelector('newsletter-popup');
    if (!popup) {
      console.warn('No <newsletter-popup> on this page - check the Overlay section group.');
      return null;
    }

    popup.open();
    return popup;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.sectionId = this.dataset.sectionId;
    this.openDelay = parseInt(this.dataset.openDelay, 10) || 0;
    this.closeDelay = parseInt(this.dataset.closeDelay, 10) || 0;
    this.countryCode = this.dataset.countryCode || '';
    this.profileCountry = this.dataset.profileCountry;

    this.inner = this.querySelector('.newsletter-popup__inner');
    this.content = this.querySelector('.newsletter-popup__content');
    this.closeButton = this.querySelector('.newsletter-popup__close');
    this.dismissButton = this.querySelector('[data-newsletter-dismiss]');
    this.emailInput = this.querySelector('[data-newsletter-email]');
    this.phoneInput = this.querySelector('[data-newsletter-phone]');
    this.emailStep = this.querySelector('[data-step="email"]');
    this.smsStep = this.querySelector('[data-step="sms"]');
    this.successState = this.querySelector('.newsletter-popup__success-state');

    // Sibling, not child: this element is hidden while the tab shows
    this.trigger = this.parentElement?.querySelector('[data-newsletter-trigger]');
    this.trigger?.querySelector('[data-newsletter-trigger-open]')?.addEventListener('click', () => {
      this.openedFromTrigger = true;
      this.open();
    });
    this.trigger
      ?.querySelector('[data-newsletter-trigger-dismiss]')
      ?.addEventListener('click', () => this.dismissTrigger());

    this.subscribedEmail = null;

    this.closeButton?.addEventListener('click', () => this.close());
    this.dismissButton?.addEventListener('click', () => this.renderSuccess());
    this.querySelectorAll('[data-step-form]').forEach((form) => {
      form.addEventListener('submit', (event) => this.handleSubmit(event));
    });

    this.querySelector('.newsletter-popup__overlay')?.addEventListener('click', () => this.close());
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.close();
    });

    [this.emailInput, this.phoneInput].forEach((input) => {
      input?.addEventListener('input', () => this.showError(input.form, null));
    });

    if (this.inner && typeof bindSwipeDownToClose === 'function') {
      bindSwipeDownToClose(this.inner, this.inner, () => this.close());
    }

    if (Shopify.designMode) {
      this.bindDesignModeListeners();
    } else {
      this.scheduleOpen();
    }

    this.updateTrigger();
  }

  /*****************************************************************
      Open / close
  *****************************************************************/

  get isOpen() {
    return !this.hasAttribute('inert');
  }

  get isSuppressed() {
    const expiry = parseInt(localStorage.getItem('newsletterPopupExpiry'), 10);
    return !!expiry && Date.now() < expiry;
  }

  scheduleOpen() {
    if (this.isSuppressed) return;
    this.openTimeout = setTimeout(() => this.open(), this.openDelay);
  }

  open() {
    if (this.isOpen) return;
    clearTimeout(this.openTimeout);

    document.querySelector('cart-drawer.active')?.close();
    document.querySelector('sidebar-menu')?.closeSidebar?.();

    document.body.classList.add('overflow-hidden');

    this.removeAttribute('inert');
    this.classList.add('open');
    this.updateTrigger();

    if (typeof trapFocus === 'function') {
      setTimeout(() => trapFocus(this, this.closeButton), 410);
    }
  }

  close() {
    clearTimeout(this.openTimeout);
    clearTimeout(this.autoCloseTimeout);

    const wasOpen = this.isOpen;
    this.setAttribute('inert', '');
    this.classList.remove('open');

    if (wasOpen) {
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
    }

    this.suppress();
    this.updateTrigger();

    if (wasOpen && this.openedFromTrigger && !this.trigger?.hidden) {
      this.trigger.querySelector('[data-newsletter-trigger-open]')?.focus();
    }
    this.openedFromTrigger = false;

    if (wasOpen) setTimeout(() => this.isOpen || this.reset(), 400);
  }

  /*****************************************************************
      Side tab
  *****************************************************************/

  get triggerAllowed() {
    if (Shopify.designMode) return true;
    if (localStorage.getItem(NEWSLETTER_SUBSCRIBED_KEY) === 'true') return false;
    // Only once the popup has been closed without subscribing
    if (localStorage.getItem('newsletterPopupClosed') !== 'true') return false;
    const hiddenUntil = parseInt(localStorage.getItem(NEWSLETTER_TRIGGER_HIDDEN_KEY), 10);
    return !(hiddenUntil && Date.now() < hiddenUntil);
  }

  updateTrigger() {
    if (!this.trigger) return;
    // While open, the popup's scroll lock slides it out in CSS
    this.trigger.hidden = !this.triggerAllowed;
  }

  dismissTrigger() {
    if (!Shopify.designMode) {
      localStorage.setItem(
        NEWSLETTER_TRIGGER_HIDDEN_KEY,
        Date.now() + NEWSLETTER_POPUP_FREQUENCY_DAYS * 24 * 60 * 60 * 1000
      );
    }
    if (!this.trigger) return;
    this.trigger.classList.add('is-leaving');
    setTimeout(() => {
      this.trigger.hidden = true;
      this.trigger.classList.remove('is-leaving');
    }, 400);
  }

  suppress() {
    if (Shopify.designMode) return;
    localStorage.setItem('newsletterPopupClosed', 'true');
    localStorage.setItem(
      'newsletterPopupExpiry',
      Date.now() + NEWSLETTER_POPUP_FREQUENCY_DAYS * 24 * 60 * 60 * 1000
    );
  }

  bindDesignModeListeners() {
    ['shopify:section:select', 'shopify:section:load'].forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        if (event.detail.sectionId === this.sectionId) {
          this.open();
          this.showEditorStep();
        } else {
          this.close();
        }
      });
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail.sectionId === this.sectionId) this.close();
    });
  }

  /*****************************************************************
      Steps
  *****************************************************************/

  showEditorStep() {
    const step = {
      email: this.emailStep,
      sms: this.smsStep,
      success: this.successState,
    }[this.dataset.editorStep];

    this.showStep(step || this.emailStep);
  }

  showStep(step) {
    const fromHeight = this.inner?.getBoundingClientRect().height;

    [this.emailStep, this.smsStep, this.successState].forEach((element) => {
      element?.classList.toggle('hidden', element !== step);
    });

    this.classList.toggle('newsletter-popup--success', step === this.successState);

    const title = step?.querySelector('h2[id]');
    if (title) this.inner?.setAttribute('aria-labelledby', title.id);

    this.animateHeight(fromHeight);
  }

  // Mobile only: the bottom sheet's top edge would otherwise snap
  animateHeight(fromHeight) {
    if (!this.inner || !fromHeight || !this.isOpen) return;
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.heightAnimation?.cancel();
    const toHeight = this.inner.getBoundingClientRect().height;
    if (Math.abs(toHeight - fromHeight) < 1) return;

    this.inner.classList.add('is-resizing');

    const animation = this.inner.animate(
      [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
      { duration: 400, easing: 'ease' }
    );
    this.heightAnimation = animation;

    // `cancel` fires after a newer animation has started
    const release = () => {
      if (this.heightAnimation !== animation) return;
      this.inner.classList.remove('is-resizing');
      this.heightAnimation = null;
    };
    animation.addEventListener('finish', release);
    animation.addEventListener('cancel', release);
  }

  reset() {
    this.subscribedEmail = null;
    this.content?.style.removeProperty('min-height');
    this.querySelectorAll('[data-step-form]').forEach((form) => {
      form.reset();
      this.showError(form, null);
    });
    this.showStep(this.emailStep);
  }

  renderSmsStep() {
    this.showStep(this.smsStep);

    if (typeof trapFocus === 'function') trapFocus(this, this.phoneInput);
  }

  renderSuccess() {
    clearTimeout(this.autoCloseTimeout);

    // Pinned before showStep so the height animation targets it
    const contentHeight = this.content?.offsetHeight;
    if (contentHeight) this.content.style.minHeight = `${contentHeight}px`;
    this.showStep(this.successState);

    if (typeof trapFocus === 'function') trapFocus(this, this.closeButton);

    if (this.closeDelay > 0) {
      this.autoCloseTimeout = setTimeout(() => this.close(), this.closeDelay);
    }
  }

  /*****************************************************************
      Validation
  *****************************************************************/

  showError(form, key) {
    form?.querySelectorAll('.newsletter-popup__messages [data-error]').forEach((message) => {
      message.classList.toggle('hidden', message.dataset.error !== key);
    });
  }

  validateEmail(form) {
    const klaviyo = window.KlaviyoNewsletter;
    const email = this.emailInput.value.trim();
    if (!email) return this.showError(form, 'empty-email'), null;
    if (!klaviyo.isValidEmail(email)) return this.showError(form, 'invalid-email'), null;
    return email;
  }

  validatePhone(form) {
    const klaviyo = window.KlaviyoNewsletter;
    const phone = klaviyo.normalizePhone(this.phoneInput.value, this.countryCode);
    if (!phone) return this.showError(form, 'empty-phone'), null;
    if (!klaviyo.isValidPhone(phone)) return this.showError(form, 'invalid-phone'), null;
    return phone;
  }

  /*****************************************************************
      Submit
  *****************************************************************/

  async handleSubmit(event) {
    event.preventDefault();

    const form = event.currentTarget;
    const isSmsStep = form.dataset.stepForm === 'sms';

    // Step 2: email is an identifier only, consent is SMS only
    let request;
    if (isSmsStep) {
      const phone = this.validatePhone(form);
      if (!phone) return;
      request = { email: this.subscribedEmail, phone, channels: ['sms'] };
    } else {
      const email = this.validateEmail(form);
      if (!email) return;
      request = { email, channels: ['email'] };
    }
    this.showError(form, null);

    const submitButton = form.querySelector('[type="submit"]');
    submitButton?.setAttribute('disabled', '');

    try {
      await window.KlaviyoNewsletter.subscribe({
        ...request,
        listId: this.dataset.listId,
        smsListId: this.dataset.smsListId,
        source: NEWSLETTER_POPUP_SOURCE,
        properties: this.profileCountry ? { country_code: this.profileCountry } : undefined,
      });
    } catch (error) {
      console.error('Klaviyo subscription error:', error);
      this.showError(form, 'submit');
      return;
    } finally {
      submitButton?.removeAttribute('disabled');
    }

    if (isSmsStep) {
      this.renderSuccess();
      return;
    }

    this.subscribedEmail = request.email;
    if (!Shopify.designMode) localStorage.setItem(NEWSLETTER_SUBSCRIBED_KEY, 'true');
    this.suppress();

    if (this.smsStep && this.phoneInput) {
      this.renderSmsStep();
    } else {
      this.renderSuccess();
    }
  }
}

customElements.define('newsletter-popup', NewsletterPopup);
